import { type FC, useState } from 'react'
import { useAccount } from '../../../../../common/useAccount'
import { useZocksAuth } from '../../../../../components/ScopedBlock/hooks'
import { Spinner } from '../../../../../components/Spinner/Spinner'
import { LOCK_ALL_OVERRIDE } from '../../../../../models/Account'
import { NotificationChannel, PastDuePolicy, type ReminderSettings, ReminderType } from '../../../../../models/Scheduling'
import { selectSafeAuthenticatedUser } from '../../../../../store/auth/authSlice'
import { useAppDispatch, useAppSelector } from '../../../../../store/hooks'
import { useGetReminderSettingsQuery, usePreviewReminderSettingsMutation, usePutReminderSettingsMutation } from '../../../../../store/scheduling/scheduling'
import { addToast } from '../../../../../store/toast/toastSlice'
import { useTracking } from '../../../../../apps/professional-app/fa/common/tracking'
import { ConfirmDialog } from '../../../../Scheduling/shared/ConfirmDialog'
import { ChooseSchedulingEmailTemplateModal } from '../EmailTemplatesSetup/ChooseSchedulingEmailTemplateModal'
import { PastDueConfirmModal } from '../PastDueConfirmModal'
import {
  formatReminderOffset,
  hasEntryMovedEarlier,
  type ReminderRowDraft,
  type ReminderSettingsDraft,
  toSettingsDraft,
  toSettingsUpdate,
  withoutSmsChannel,
} from '../reminderDrafts'
import { type SmsChannelState } from '../ReminderSettingsCard'
import { SettingsSection } from '../SettingsSection'
import { ReminderCard, type ReminderCardCopy, type ReminderCardTemplates } from './ReminderCard'
import { type PendingConfirm, reminderSaveEvents, SAVE_ERROR_MESSAGE, TURN_OFF_MODAL_COPY } from './reminderSaveEvents'
import { draftAfterPartialSave, needsSettingsSave, savableDraft, planTemplateWrites, entryIdsByRowKey } from './reminderTemplates'
import { useReminderTemplates } from './useReminderTemplates'

const COPY: Record<ReminderType, ReminderCardCopy> = {
  [ReminderType.FOLLOW_UP]: {
    title: 'Booking follow-ups',
    heading: "Follow up when a client hasn't booked",
    description: "Remind clients who received a booking link but haven't booked yet. Follow-ups stop once they book.",
    noun: 'follow-up',
    banner: "Follow-ups aren't sent when you copy the invite or link, or save the email as a draft.",
    trailingCopy: 'after the invite is sent',
    addLabel: 'Add follow-up',
    removeLabel: 'Remove follow-up',
    removeLastTooltip: 'Turn follow-ups off to stop sending them',
  },
  [ReminderType.MEETING]: {
    title: 'Meeting reminders',
    heading: 'Send reminders before meetings',
    description: 'Remind clients about an upcoming meeting.',
    noun: 'reminder',
    trailingCopy: 'before the meeting',
    addLabel: 'Add reminder',
    removeLabel: 'Remove reminder',
    removeLastTooltip: 'Turn reminders off to stop sending them',
  },
}

const stripTexts = (settings: ReminderSettings): ReminderSettings => ({
  ...settings,
  entries: settings.entries.map((entry) => ({ ...entry, channels: entry.channels.filter((channel) => channel !== NotificationChannel.SMS) })),
})

const rowLabel = (reminderType: ReminderType, row: ReminderRowDraft) => `${formatReminderOffset(row.offsetMinutes)} ${COPY[reminderType].trailingCopy}`

interface OpenPicker {
  reminderType: ReminderType
  row: ReminderRowDraft
}

export interface RemindersSetupSectionProps {
  readOnly: boolean
  smsChannel: SmsChannelState
  onShowTextMessages: () => void
}

export const RemindersSetupSection: FC<RemindersSetupSectionProps> = ({ readOnly, smsChannel, onShowTextMessages }) => {
  const accountId = useAppSelector(selectSafeAuthenticatedUser).accountId
  const dispatch = useAppDispatch()
  const { hasScope } = useZocksAuth()
  const { account } = useAccount()
  const { data, isLoading, isError } = useGetReminderSettingsQuery({ accountId })
  const [editingType, setEditingType] = useState<ReminderType | null>(null)
  const [draft, setDraft] = useState<ReminderSettingsDraft | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [pendingConfirm, setPendingConfirm] = useState<PendingConfirm | null>(null)
  const [picker, setPicker] = useState<OpenPicker | null>(null)
  const [savedSettings, setSavedSettings] = useState<ReminderSettings | null>(null)
  const [isWritingTemplates, setIsWritingTemplates] = useState(false)
  const [putReminderSettings, { isLoading: isSaving }] = usePutReminderSettingsMutation()
  const [previewReminderSettings, { isLoading: isPreviewing }] = usePreviewReminderSettingsMutation()
  const { trackingClient } = useTracking()

  const followUp = data?.data.find((settings) => settings.reminderType === ReminderType.FOLLOW_UP)
  const savedMeetingReminders = data?.data.find((settings) => settings.reminderType === ReminderType.MEETING)
  const reminderTemplates = useReminderTemplates([followUp, savedMeetingReminders].filter((settings): settings is ReminderSettings => settings != null))

  const hasTemplateAdminScope = hasScope('**', 'iph:admin')
  const isOverrideLocked = account == null || account.enterpriseConfig.lockTemplateOverride === LOCK_ALL_OVERRIDE
  const canChooseTemplates = hasTemplateAdminScope && !isOverrideLocked

  if (isLoading || reminderTemplates.isLoading) {
    return (
      <div className='d-flex justify-content-center py-5'>
        <Spinner />
      </div>
    )
  }

  const meetingReminders = savedMeetingReminders != null && smsChannel === 'hidden' ? withoutSmsChannel(savedMeetingReminders) : savedMeetingReminders

  if (isError || data == null || followUp == null || meetingReminders == null) {
    return <div className='paragraph-small zocks-text-gray-600'>Couldn&apos;t load reminder settings. Please refresh to try again.</div>
  }

  const settingsFor = (reminderType: ReminderType) =>
    savedSettings?.reminderType === reminderType ? savedSettings : reminderType === ReminderType.FOLLOW_UP ? followUp : meetingReminders

  const startEditing = (settings: ReminderSettings) => {
    setError(null)
    setEditingType(settings.reminderType)
    setDraft(toSettingsDraft(smsChannel === 'off' && settings.reminderType === ReminderType.MEETING ? stripTexts(settings) : settings))
    setPendingConfirm(null)
  }

  const cancelEditing = () => {
    setError(null)
    setSavedSettings(null)
    setEditingType(null)
    setDraft(null)
    setPendingConfirm(null)
  }

  const handleSave = async (pastDuePolicy: PastDuePolicy = PastDuePolicy.SEND) => {
    if (draft == null || editingType == null) {
      return
    }
    const settings = settingsFor(editingType)
    let savedEntries = settings.entries
    if (needsSettingsSave(draft, settings)) {
      const pastDueCount = pendingConfirm?.kind === 'past-due' ? pendingConfirm.pastDueCount : undefined
      try {
        const saved = await putReminderSettings({
          accountId,
          reminderType: editingType,
          update: toSettingsUpdate(savableDraft(draft, settings), pastDuePolicy),
        }).unwrap()
        savedEntries = saved.entries
        setSavedSettings({ ...settings, ...saved })
        reminderSaveEvents({ reminderType: editingType, draft, settings, pastDuePolicy, pastDueCount }).forEach((event) => trackingClient.track(event))
      } catch {
        setError(SAVE_ERROR_MESSAGE)
        setPendingConfirm(null)
        return
      }
    }
    setPendingConfirm(null)
    const idsByRowKey = entryIdsByRowKey(draft, savedEntries)
    const writes = planTemplateWrites(draft, idsByRowKey, reminderTemplates.choices, reminderTemplates.isZocksDefault)
    setIsWritingTemplates(true)
    const failedRowKeys = await reminderTemplates.writeTemplates(editingType, writes)
    setIsWritingTemplates(false)
    if (failedRowKeys.length > 0) {
      const failedRows = draft.rows.filter((row) => failedRowKeys.includes(row.key)).map((row) => rowLabel(editingType, row))
      setDraft(draftAfterPartialSave(draft, idsByRowKey, failedRowKeys))
      setError(`Couldn't save the email template for ${failedRows.join(', ')}. Your other changes are saved. Please try again.`)
      return
    }
    setError(null)
    setSavedSettings(null)
    setEditingType(null)
    setDraft(null)
    dispatch(addToast({ variant: 'success', title: 'Reminders saved' }))
  }

  const requestSave = async () => {
    if (draft == null || editingType == null) {
      return
    }
    const settings = settingsFor(editingType)
    if (settings.enabled && !draft.enabled) {
      setPendingConfirm({ kind: 'turn-off' })
      return
    }
    if (!draft.enabled || !hasEntryMovedEarlier(draft, settings)) {
      await handleSave()
      return
    }
    const pastDueCount = await previewReminderSettings({ accountId, reminderType: editingType, update: toSettingsUpdate(draft, PastDuePolicy.SEND) })
      .unwrap()
      .then((preview) => preview.pastDueCount)
      .catch(() => 0)
    if (pastDueCount > 0) {
      setPendingConfirm({ kind: 'past-due', pastDueCount })
    } else {
      await handleSave()
    }
  }

  const templatesFor = (reminderType: ReminderType): ReminderCardTemplates => ({
    nameFor: (row) => {
      if (reminderTemplates.isError) {
        return "Couldn't load the email template"
      }
      const { template, isUnavailable } = reminderTemplates.templateFor(reminderType, row)
      return template?.name ?? (isUnavailable ? 'Unavailable template' : 'Zocks default')
    },
    canChoose: (row) => canChooseTemplates && !reminderTemplates.isError && !reminderTemplates.isEnforced(row),
    isLocked: (row) => hasTemplateAdminScope && (isOverrideLocked || reminderTemplates.isEnforced(row)),
    onChoose: (row) => setPicker({ reminderType, row }),
  })

  const chooseTemplate = (openPicker: OpenPicker, templateId: string) => {
    const savedChoiceId = openPicker.row.id != null ? reminderTemplates.choices[openPicker.row.id]?.templateId : undefined
    const unchanged = savedChoiceId != null ? templateId === savedChoiceId : reminderTemplates.isZocksDefault(templateId)
    setDraft((current) =>
      current != null
        ? { ...current, rows: current.rows.map((row) => (row.key === openPicker.row.key ? { ...row, templateId: unchanged ? undefined : templateId } : row)) }
        : current,
    )
  }

  const editDisabledTooltip = editingType === ReminderType.FOLLOW_UP ? 'Finish editing booking follow-ups first' : 'Finish editing meeting reminders first'
  const isBusy = isSaving || isPreviewing || isWritingTemplates
  const pickerEmail = picker != null ? reminderTemplates.emailFor(picker.reminderType) : undefined

  return (
    <SettingsSection
      title='Reminders'
      subtitle='Choose when clients get reminders about meetings booked through Scheduling. These settings apply to all event types in the account.'
      isSaving={isBusy}
    >
      {[settingsFor(ReminderType.FOLLOW_UP), settingsFor(ReminderType.MEETING)].map((settings) => (
        <ReminderCard
          key={settings.reminderType}
          copy={COPY[settings.reminderType]}
          settings={settings}
          draft={editingType === settings.reminderType ? draft : null}
          smsChannel={settings.reminderType === ReminderType.FOLLOW_UP ? 'hidden' : smsChannel}
          readOnly={readOnly}
          editDisabled={editingType != null && editingType !== settings.reminderType}
          editDisabledTooltip={editDisabledTooltip}
          isSaving={isBusy}
          error={editingType === settings.reminderType ? error : null}
          templates={templatesFor(settings.reminderType)}
          onEdit={() => startEditing(settings)}
          onCancel={cancelEditing}
          onSave={requestSave}
          onDraftChange={setDraft}
          onShowTextMessages={onShowTextMessages}
        />
      ))}
      {picker != null && pickerEmail != null ? (
        <ChooseSchedulingEmailTemplateModal
          email={pickerEmail}
          currentTemplateId={reminderTemplates.templateFor(picker.reminderType, picker.row).template?.id}
          mode='choose'
          subtitle={`For the ${COPY[picker.reminderType].noun} ${rowLabel(picker.reminderType, picker.row)}`}
          openTemplateInNewTab
          onSelect={async (template) => chooseTemplate(picker, template.id)}
          onHide={() => setPicker(null)}
        />
      ) : null}
      {pendingConfirm?.kind === 'turn-off' && editingType != null ? (
        <ConfirmDialog
          title={TURN_OFF_MODAL_COPY[editingType].title}
          body={TURN_OFF_MODAL_COPY[editingType].body}
          confirmLabel='Turn off'
          confirmVariant='primary'
          isLoading={isSaving}
          onConfirm={handleSave}
          onCancel={() => setPendingConfirm(null)}
        />
      ) : null}
      {pendingConfirm?.kind === 'past-due' && editingType != null ? (
        <PastDueConfirmModal
          reminderType={editingType}
          pastDueCount={pendingConfirm.pastDueCount}
          isSaving={isBusy}
          onConfirm={handleSave}
          onCancel={() => setPendingConfirm(null)}
        />
      ) : null}
    </SettingsSection>
  )
}
